import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { MAX_UNACKNOWLEDGED_ITEMS, ARCHIVE_THRESHOLD, fromPersisted, fromGenerated } from "./streamModel";

const sameItem = (item, idx, target, index) => (target.activityId ? item.activityId === target.activityId : idx === index);

// Stream feed: persisted load, autonomous generation cadence, acknowledge/review/execute status, auto-archive.
export const useStreamFeed = ({ userId, updates, setUpdates, isPaused }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [needsAcknowledgment, setNeedsAcknowledgment] = useState(false);
  const [archiveStats, setArchiveStats] = useState({ totalArchived: 0, reviewedNotArchived: 0 });
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const hasLoadedPersistedData = useRef(false);

  const unacknowledgedCount = updates.filter((u) => u.status === "new").length;
  const reviewedCount = updates.filter((u) => ["viewed", "executed"].includes(u.status) && !u.isArchived).length;

  const saveStreamItems = useCallback(async (items) => {
    try {
      await axios.post(`${API}/stream/save/${userId}`, items);
    } catch (error) {
      console.error("Error saving stream items:", error);
    }
  }, [userId]);

  const fetchArchiveStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stream/archive-stats/${userId}`);
      setArchiveStats(response.data);
    } catch (error) {
      console.error("Error fetching archive stats:", error);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId || hasLoadedPersistedData.current) return;
    hasLoadedPersistedData.current = true;
    (async () => {
      try {
        const response = await axios.get(`${API}/stream/${userId}?limit=50`);
        if (!response.data?.length) return;
        const persisted = response.data.map(fromPersisted);
        setUpdates((prev) => {
          const existing = new Set(prev.map((p) => p.activityId));
          return [...prev, ...persisted.filter((p) => !existing.has(p.activityId))].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        });
      } catch (error) {
        console.error("Error loading persisted stream items:", error);
      }
    })();
  }, [userId, setUpdates]);

  useEffect(() => {
    if (unacknowledgedCount >= MAX_UNACKNOWLEDGED_ITEMS) setNeedsAcknowledgment(true);
  }, [unacknowledgedCount]);

  useEffect(() => { fetchArchiveStats(); }, [fetchArchiveStats]);

  useEffect(() => {
    if (reviewedCount < ARCHIVE_THRESHOLD) return;
    (async () => {
      try {
        const response = await axios.post(`${API}/stream/archive/${userId}`);
        if (response.data.archivedCount > 0) {
          setUpdates((prev) => prev.filter((item) => !["viewed", "executed"].includes(item.status)));
          toast.success(`${response.data.archivedCount} items archived`, { description: "Reviewed items have been moved to the archive" });
          fetchArchiveStats();
        }
      } catch (error) {
        console.error("Error auto-archiving:", error);
      }
    })();
  }, [reviewedCount, userId, setUpdates, fetchArchiveStats]);

  useEffect(() => {
    const generateActivity = async () => {
      if (isPaused || needsAcknowledgment) return;
      setIsGenerating(true);
      try {
        const response = await axios.post(`${API}/agent/auto-activity/${userId}`);
        const activityUpdate = fromGenerated(response.data.data);
        setUpdates((prev) => {
          const next = [activityUpdate, ...prev.slice(0, 49)];
          if (next.filter((u) => u.status === "new").length >= MAX_UNACKNOWLEDGED_ITEMS) setNeedsAcknowledgment(true);
          saveStreamItems([activityUpdate]);
          return next;
        });
      } catch (error) {
        console.error("Error generating activity:", error);
      }
      setIsGenerating(false);
    };

    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (!isPaused && !needsAcknowledgment) {
      timeoutRef.current = setTimeout(generateActivity, 3000);
      intervalRef.current = setInterval(() => setTimeout(generateActivity, 90000 + Math.random() * 60000), 120000);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [userId, isPaused, needsAcknowledgment, saveStreamItems, setUpdates]);

  const acknowledgeAll = () => {
    const now = new Date().toISOString();
    setUpdates((prev) => {
      const updated = prev.map((item) => (item.status === "new" ? { ...item, status: "viewed", viewedAt: now } : item));
      saveStreamItems(updated.filter((item) => item.status === "viewed" && !item.isArchived));
      return updated;
    });
    setNeedsAcknowledgment(false);
    toast.success("Stream acknowledged - generating new insights");
  };

  const markStatus = (target, index, patch) => {
    setUpdates((prev) => {
      const updated = prev.map((item, idx) => (sameItem(item, idx, target, index) ? { ...item, ...patch(item) } : item));
      const changed = updated.find((item, idx) => sameItem(item, idx, target, index));
      if (changed) saveStreamItems([changed]);
      return updated;
    });
  };

  const markReviewed = (update, index) => markStatus(update, index, (item) => ({ status: item.status === "executed" ? "executed" : "viewed", viewedAt: new Date().toISOString() }));
  const markExecuted = (update, index) => markStatus(update, index, () => ({ status: "executed", executedAt: new Date().toISOString() }));
  const markAnswered = (questionId) => setUpdates((prev) => prev.map((item) => (item.questionId === questionId ? { ...item, status: "answered" } : item)));

  return { isGenerating, needsAcknowledgment, unacknowledgedCount, reviewedCount, archiveStats, fetchArchiveStats, acknowledgeAll, markReviewed, markExecuted, markAnswered };
};

// Archive tab: search + date-grouped items.
export const useArchive = (userId, active, search) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedDates, setExpandedDates] = useState({});

  useEffect(() => {
    if (!active) return;
    (async () => {
      setLoading(true);
      try {
        const params = search ? `?search=${encodeURIComponent(search)}` : "";
        const response = await axios.get(`${API}/stream/archived/${userId}${params}`);
        const dateGroups = response.data.dateGroups || [];
        setGroups(dateGroups);
        if (dateGroups.length > 0) setExpandedDates((prev) => ({ ...prev, [dateGroups[0].date]: true }));
      } catch (error) {
        console.error("Error fetching archived items:", error);
      }
      setLoading(false);
    })();
  }, [userId, active, search]);

  const toggleDate = (date) => setExpandedDates((prev) => ({ ...prev, [date]: !prev[date] }));
  return { groups, loading, expandedDates, toggleDate };
};
